import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Modal from "../components/ui/Modal";
import { from } from '@apollo/client';

const MobilierHabitatDetail = () => {
    const { id } = useParams();
    const [mobilierHabitat, setMobilierHabitat] = useState(null);
    const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);
    const [quoteForm, setQuoteForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
    });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    useEffect(() => {
        const fetchMobilierHabitat = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/furnitures/${id}?populate=*`, {
                    headers: {
                        Authorization: `${process.env.REACT_APP_API_TOKEN}`
                    }
                });
                // Vérification et normalisation des données
                const data = response.data.data;
                setMobilierHabitat({
                    title: data.title || 'Sans titre',
                    author: data.author || 'Auteur inconnu',
                    date: data.date || 'Date non spécifiée',
                    category: data.category || 'Catégorie non spécifiée',
                    description: data.description || 'Aucune description disponible',
                    subtitle: data.subtitle || '',
                    content: data.content || [],
                    imageArticle: data.imageArticle || {
                        url: '/placeholder-image.jpg' // Image par défaut
                    },
                    additionalImages: data.additionalImages || []
                });
                setQuoteForm(prev => ({
                    ...prev,
                    message: `Je souhaite obtenir un devis pour l'article ${data.title || 'Sans titre'}`
                }));
            } catch (error) {
                console.error("Error fetching article:", error);
            }
        };

        fetchMobilierHabitat();
    }, [id]);

    if (!mobilierHabitat) return <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-gray-900"></div>
    </div>;


    const {
        title = 'Sans titre',
        author = 'Auteur inconnu',
        date = 'Date non spécifiée',
        category = 'Catégorie non spécifiée',
        description = 'Aucune description disponible',
        subtitle = '',
        content = [],
        imageArticle = { url: '/placeholder-image.jpg' },
        additionalImages = []
    } = mobilierHabitat;


    const handleQuoteSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);
        try {

            await axios.post(`${process.env.REACT_APP_API_URL}/api/email`, {
                to: "contact@isiliving.fr",
                from: "iannis.taioglou@gmail.com",
                subject: `Demande de devis - ${title}`,
                text: `Nouvelle demande de devis pour ${title} de ${quoteForm.firstName} ${quoteForm.lastName}`,
                html: `
                    <h2>Nouvelle demande de devis</h2>
                    <p><strong>Article:</strong> ${title}</p>
                    <p><strong>Nom:</strong> ${quoteForm.lastName || 'Non renseigné'}</p>
                    <p><strong>Prénom:</strong> ${quoteForm.firstName || 'Non renseigné'}</p>
                    <p><strong>Email:</strong> ${quoteForm.email || 'Non renseigné'}</p>
                    <p><strong>Téléphone:</strong> ${quoteForm.phone || 'Non renseigné'}</p>
                    <p><strong>Message:</strong> ${quoteForm.message || 'Aucun message'}</p>
                `
            });
            // Réinitialisation du formulaire
            setQuoteForm({
                firstName: '',
                lastName: '',
                email: '',
                phone: '',
                message: `Je souhaite obtenir un devis pour l'article ${title}`
            });

            // Fermeture du modal de formulaire et ouverture du modal de confirmation
            setIsQuoteModalOpen(false);
            setIsModalOpen(true);
        } catch (error) {
            // Gestion des erreurs
            console.error('Erreur lors de l\'envoi du devis:', error);
            if (error.message.includes("Veuillez")) {
                // C'est une erreur de validation locale
                setSubmitError(error.message);
            } else {
                // C'est une erreur de l'API
                setSubmitError("Une erreur est survenue lors de l'envoi du message. Veuillez réessayer plus tard.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream">
            {/* Hero Section */}
            <section className="relative h-[80vh]">
                <img
                    src={`${imageArticle.url}`}
                    alt={title}
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30" />

                {/* Navigation */}
                <div className="absolute top-8 left-8 z-20">
                    <Link
                        to="/categories/mobilier/habitat"
                        className="flex items-center gap-2 text-cream hover:text-brandy-rose transition-colors"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                        <span>Retour</span>
                    </Link>
                </div>

                {/* Hero Content */}
                <div className="absolute inset-0 flex items-center justify-center text-center p-4">
                    <div className="max-w-4xl">
                        <motion.h1
                            className="text-4xl md:text-6xl font-cormorant text-cream mb-6"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                        >
                            {title}
                        </motion.h1>
                        <motion.div
                            className="text-lg text-cream/80"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            <span className="mr-4">Par {author}</span>
                            <span className="mr-4">•</span>
                            <span>{category}</span>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Content Section */}
            <section className="max-w-4xl mx-auto px-4 py-16">
                {/* Description */}
                <div className="prose prose-lg mx-auto">
                    {content && content.map((block, index) => {
                        switch (block.type) {
                            case 'paragraph':
                                return (
                                    <div key={index} className="mb-8">
                                        {block.children && block.children.map((child, childIndex) => {
                                            switch (child.type) {
                                                case 'text':
                                                    return (
                                                        <p
                                                            key={childIndex}
                                                            className={`
                                                text-gray-600
                                                ${child.bold ? 'font-bold' : ''}
                                                ${child.italic ? 'italic' : ''}
                                                ${child.underline ? 'underline' : ''}
                                            `}
                                                        >
                                                            {child.text}
                                                        </p>
                                                    );
                                                case 'link':
                                                    return (
                                                        <a
                                                            key={childIndex}
                                                            href={child.url}
                                                            className="text-cocoa hover:text-brandy-rose transition-colors underline"
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            {child.children[0].text}
                                                        </a>
                                                    );
                                                default:
                                                    return null;
                                            }
                                        })}
                                    </div>
                                );
                            case 'heading':
                                return (
                                    <h2 key={index} className="text-3xl font-cormorant text-cocoa mb-6">
                                        {block.children[0].text}
                                    </h2>
                                );
                            case 'list':
                                return (
                                    <ul key={index} className="list-disc pl-6 mb-6 text-gray-600">
                                        {block.children.map((item, itemIndex) => (
                                            <li key={itemIndex} className="mb-2">
                                                {item.children[0].text}
                                            </li>
                                        ))}
                                    </ul>
                                );
                            default:
                                return null;
                        }
                    })}
                </div>

                {/* Image Gallery */}
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                >
                    {additionalImages && additionalImages.map((image, index) => (
                        <motion.div
                            key={index}
                            className="aspect-w-4 aspect-h-3 overflow-hidden rounded-lg"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.2 }}
                            onClick={() => setSelectedImage(`${image.url}`)}
                        >
                            <img
                                src={`${image.url}`}
                                alt={`Vue ${index + 1}`}
                                className="w-full h-full object-cover"
                            />
                        </motion.div>
                    ))}
                </motion.div>

                {/* Image Modal */}
                {selectedImage && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
                        onClick={() => setSelectedImage(null)}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className="relative max-w-7xl max-h-[90vh] w-full h-full"
                        >
                            <img
                                src={selectedImage}
                                alt="Image agrandie"
                                className="w-full h-full object-contain"
                            />
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-4 right-4 text-white p-2 hover:text-brandy-rose transition-colors"
                            >
                                <span className="text-2xl">×</span>
                            </button>
                        </motion.div>
                    </div>
                )}

                {/* Prix et CTA */}
                <motion.div
                    className="text-center mt-16"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                >
                    <button
                        onClick={() => setIsQuoteModalOpen(true)}
                        className="bg-cocoa text-cream px-8 py-3 rounded-full hover:bg-brandy-rose transition-colors"
                    >
                        Demander un devis
                    </button>
                </motion.div>
            </section>

            {/* Quote Modal */}
            {isQuoteModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <motion.div
                        className="bg-cream rounded-lg p-8 max-w-2xl w-full"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <h2 className="text-3xl font-light text-cocoa mb-6">Demande de devis - {title}</h2>
                        <form onSubmit={handleQuoteSubmit} className="space-y-4">
                            {submitError && (
                                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                                    <span className="block sm:inline">{submitError}</span>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    placeholder="Prénom *"
                                    required
                                    className="p-3 border rounded-lg bg-white"
                                    value={quoteForm.firstName}
                                    onChange={(e) => setQuoteForm({ ...quoteForm, firstName: e.target.value })}
                                />
                                <input
                                    type="text"
                                    placeholder="Nom *"
                                    required
                                    className="p-3 border rounded-lg bg-white"
                                    value={quoteForm.lastName}
                                    onChange={(e) => setQuoteForm({ ...quoteForm, lastName: e.target.value })}
                                />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="email"
                                    placeholder="Email *"
                                    required
                                    className="p-3 border rounded-lg bg-white"
                                    value={quoteForm.email}
                                    onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                                />
                                <input
                                    type="tel"
                                    placeholder="Téléphone *"
                                    required
                                    className="p-3 border rounded-lg bg-white"
                                    value={quoteForm.phone}
                                    onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                                />
                            </div>
                            <textarea
                                placeholder="Votre message"
                                rows="4"
                                className="w-full p-3 border rounded-lg bg-white"
                                value={quoteForm.message}
                                onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                            />
                            <p className="text-sm text-gray-600">* Champs obligatoires</p>
                            <div className="flex justify-end space-x-4 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsQuoteModalOpen(false)}
                                    className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                                    disabled={isSubmitting}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-3 bg-cocoa text-white rounded-lg hover:bg-brandy-rose transition-colors flex items-center"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            Envoi en cours...
                                        </>
                                    ) : (
                                        'Envoyer la demande'
                                    )}
                                </button>
                            </div>
                        </form>
                    </motion.div>
                </div>
            )}

            {/* Success Modal */}
            {isModalOpen && (
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title="Demande envoyée"
                    message="Votre demande de devis a bien été envoyée. Notre équipe vous contactera dans les plus brefs délais."
                />
            )}
        </div>
    );
};

export default MobilierHabitatDetail;